import React, { useState, useEffect, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useParams,
  useLocation,
} from "react-router-dom";
import { storage } from "./utils/storage";
import { dataService } from "./services/dataService"; // API integration service
import { cmsService } from "./services/cmsService"; // CMS integration service
import TopBar from "./components/TopBar";
import AudioBar from "./components/AudioBar";
import ContentSlide from "./components/ContentSlide";
import QuizSlide from "./components/QuizSlide";
import SuccessSlide from "./components/SuccessSlide";
import ExitConfirmation from "./components/ExitConfirmation";
import LandscapeWarning from "./components/LandscapeWarning";
import ErrorDisplay from "./components/ErrorDisplay";
import LoadingScreen from "./components/LoadingScreen";
import "./App.css";

// Main App Component with routing
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<ElearningModule />} />
        <Route path="/index.html" element={<ElearningModule />} />
        <Route
          path="/elearning/module/:moduleId"
          element={<ElearningModule />}
        />
        <Route path="/elearning/module/" element={<ElearningModule />} />
        <Route path="*" element={<ElearningModule />} />
      </Routes>
    </Router>
  );
}

// Main E-Learning Module Component
function ElearningModule() {
  const { moduleId: urlModuleId } = useParams();
  const location = useLocation();

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [currentLanguage, setCurrentLanguage] = useState("en");
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isLandscape, setIsLandscape] = useState(true);
  const [audioCompleted, setAudioCompleted] = useState({});
  const [quizStateUpdate, setQuizStateUpdate] = useState(0); // Trigger for quiz state changes
  const [apiQuizStates, setApiQuizStates] = useState({}); // Quiz states from API only
  const [stopAudioFunction, setStopAudioFunction] = useState(null);
  const [quizShowingResults, setQuizShowingResults] = useState(false);
  const [topBarVisible, setTopBarVisible] = useState(true);
  const [audioBarVisible, setAudioBarVisible] = useState(true);
  const [showSuccessSlide, setShowSuccessSlide] = useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [lessonData, setLessonData] = useState(null);
  const [moduleId, setModuleId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [errorType, setErrorType] = useState("elearning/app/default");
  const [currentAccessToken, setCurrentAccessToken] = useState(null);
  const [startSlide, setStartSlide] = useState(1); // Track the initial start slide from API
  // eslint-disable-next-line no-unused-vars

  // Make CMS test function available globally for debugging (development only)
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      window.testErrorMessages = cmsService.testErrorMessages;
      window.testCmsConfig = cmsService.testCmsConfig;
      console.log("Debug functions: testErrorMessages(), testCmsConfig()");
    }
  }, []);
  // Wrapper: From success overlay, go to slide 1 and hide success overlay
  const handleGoToSlide1FromSuccess = () => {
    setShowSuccessSlide(false);
    handleGoToSlide1();
  };

  // Get both module ID, auth code, and locale from URL parameters
  const getURLParameters = useCallback(() => {
    const urlParams = new URLSearchParams(location.search);

    // Get module ID from route parameter first, then query parameter as fallback
    let moduleId = urlModuleId;
    if (!moduleId) {
      moduleId = urlParams.get("moduleId");
    }
    if (!moduleId) {
      moduleId = "default";
    }

    // Get auth code from query parameters
    const authCode = urlParams.get("auth_code");

    // Get locale from query parameters
    const locale = urlParams.get("locale");

    return {
      moduleId,
      authCode,
      locale,
    };
  }, [urlModuleId, location.search]);

  // Handle bar visibility changes
  const handleTopBarVisibilityChange = (isVisible) => {
    setTopBarVisible(isVisible);
  };

  const handleAudioBarVisibilityChange = (isVisible) => {
    setAudioBarVisible(isVisible);
  };

  // Compute app CSS classes based on bar visibility
  const getAppClasses = () => {
    const classes = ["app"];

    if (!topBarVisible && !audioBarVisible) {
      classes.push("both-bars-hidden");
    } else {
      if (!topBarVisible) {
        classes.push("top-bar-hidden");
      }
      if (!audioBarVisible) {
        classes.push("audio-bar-hidden");
      }
    }

    return classes.join(" ");
  };

  // Get quiz result display state
  const getQuizAudioDisabled = () => {
    // Only disable audio for quiz slides when showing results
    if (!currentSlide?.IsQuiz) return false;
    return quizShowingResults;
  };

  // Map current language to the JSON language codes
  const getLanguageCode = (language) => {
    const languageMap = {
      en: "En",
      ar: "Ar",
      ur: "Ur",
      ma: "Ma",
    };
    return languageMap[language] || "En";
  };

  // Validate locale from URL - only en, ar, ur, ma are accepted
  // Also accept 'ml' (ISO 639-1 Malayalam code) and map it to 'ma'
  const validateLocale = (locale) => {
    const validLocales = ["en", "ar", "ur", "ma"];
    const localeMapping = {
      "ml": "ma" // Map ISO 639-1 Malayalam code to internal code
    };
    
    if (!locale) {
      return { valid: true, locale: "en" }; // Default to 'en' if no locale provided
    }
    
    const normalizedLocale = locale.toLowerCase();
    
    // Check if it's a mapped locale first
    if (localeMapping[normalizedLocale]) {
      return { valid: true, locale: localeMapping[normalizedLocale] };
    }
    
    if (validLocales.includes(normalizedLocale)) {
      return { valid: true, locale: normalizedLocale };
    }
    
    return { valid: false, locale: null };
  };

  // Helper function to get all quiz statuses from local storage in the format: {5: {attempts: 2, status: "passed"}}
  const getAllQuizStatusesFromStorage = useCallback(() => {
    return storage.getAllQuizStatuses();
  }, []);

  // Helper function to get completed slides from local storage in the format: [1, 2, 3, 4, 5]
  const getCompletedSlidesFromStorage = useCallback(() => {
    return storage.getCompletedSlides();
  }, []);

  // Helper function to get isallSlidesCompleted from storage
  const getIsAllSlidesCompletedFromStorage = useCallback(() => {
    return storage.getIsAllSlidesCompleted();
  }, []);

  // Testing utilities removed for production

  // Initialize app function - moved outside useEffect so it can be called from error handler
  const initializeApp = useCallback(async () => {
    try {
      setIsLoading(true);
      setApiError(null);

      // Add minimum loading time to prevent flashing (1.5 seconds)
      const minLoadingTime = new Promise((resolve) =>
        setTimeout(resolve, 1500)
      );

      // Get module ID, auth code, and locale from URL
      const {
        moduleId: currentModuleId,
        authCode,
        locale,
      } = getURLParameters();

      // Validate locale - must be one of: en, ar, ur, ma
      // If no locale is provided after auth_code, default to 'en'
      const localeValidation = validateLocale(locale);
      if (!localeValidation.valid) {
        await minLoadingTime;
        const errorData = await cmsService.getErrorMessage(
          "elearning/app/callback/invalidlocale",
          "en"
        );
        setApiError(errorData);
        setErrorType("elearning/app/callback/invalidlocale");
        setIsLoading(false);
        return;
      }

      const validatedLocale = localeValidation.locale;

      if (!currentModuleId) {
        await minLoadingTime;
        const errorData = await cmsService.getErrorMessage(
          "elearning/app/callback/404",
          validatedLocale
        );
        setApiError(errorData);
        setErrorType("elearning/app/callback/404");
        setIsLoading(false);
        return;
      }

      console.log(
        "App: Initializing with moduleId:",
        currentModuleId,
        "authCode:",
        authCode ? "present" : "not present",
        "locale:",
        validatedLocale
      );
      setModuleId(currentModuleId);

      // ✅ INITIALIZE STORAGE IMMEDIATELY AFTER GETTING MODULEID (before any storage operations)
      if (currentModuleId) {
        storage.initialize(currentModuleId);
        console.log(`App: Storage initialized for module ${currentModuleId}`);
      }

      // Set the validated locale as the current language
      setCurrentLanguage(validatedLocale);

      // Initialize data service with auth code and locale
      const initResult = await dataService.initialize(
        currentModuleId,
        authCode,
        validatedLocale
      );

      if (!initResult.success) {
        await minLoadingTime;
        
        // Use dynamic error code from backend if available, otherwise fallback
        const errorCode = initResult.code || "elearning/app/callback/authmissing";
        
        const errorData = await cmsService.getErrorMessage(
          errorCode,
          validatedLocale
        );
        setApiError(errorData);
        setErrorType(errorCode);
        setIsLoading(false);
        return;
      }

      // Store the access token in React state
      setCurrentAccessToken(initResult.accessToken);
      console.log("App: Access token obtained and stored in state");

      // Load module data using the new token
      const accessToken = initResult.accessToken;
      const apiData = await dataService.loadModuleData(
        currentModuleId,
        accessToken
      );

      if (apiData.success) {
        // Ensure minimum loading time has passed before showing content
        await minLoadingTime;

        // Set data from API response
        setLessonData(apiData.lessonData);
        setCurrentSlideIndex(apiData.currentSlide);
        // Use validated locale from URL instead of API response language
        // The locale from URL takes precedence
        setCurrentLanguage(validatedLocale);

        // Use dynamic module ID from API response
        const finalModuleId = apiData.moduleId || currentModuleId;
        if (apiData.moduleId && apiData.moduleId !== currentModuleId) {
          setModuleId(apiData.moduleId);
          // ✅ RE-INITIALIZE STORAGE IF MODULEID CHANGES FROM API
          storage.initialize(apiData.moduleId);
          console.log(
            `App: Storage re-initialized with API moduleId ${apiData.moduleId} (was ${currentModuleId})`
          );
        }

        // Initialize states with completion data from API
        const completedSlides = apiData.userProgress?.completedSlides || {};
        const completedQuizzes = apiData.userProgress?.completedQuizzes || {};
        const apiStartSlide = apiData.userProgress?.startSlide || 1;

        // Log the enhanced tracking data
        console.log("App: Enhanced tracking data from API:", {
          completedSlides,
          completedQuizzes,
          apiCompletedSlides: apiData.userProgress?.apiCompletedSlides,
          apiAllQuizStatuses: apiData.userProgress?.apiAllQuizStatuses,
          startSlide: apiStartSlide,
        });

        // Initialize local storage with enhanced API data using merge functions
        const completedSlidesArray = [];
        Object.keys(completedSlides).forEach((slideNumber) => {
          if (completedSlides[slideNumber] === true) {
            completedSlidesArray.push(parseInt(slideNumber));
          }
        });

        // Add slides before startSlide as completed
        for (let i = 1; i < apiStartSlide; i++) {
          if (!completedSlidesArray.includes(i)) {
            completedSlidesArray.push(i);
          }
        }

        // Use override functions to completely replace localStorage with API data
        const overriddenCompletedSlides =
          storage.overrideWithApiCompletedSlides(
            apiData.userProgress?.apiCompletedSlides || completedSlidesArray
          );

        // Initialize quiz statuses with override function
        const quizStatuses = {};
        Object.keys(completedQuizzes).forEach((slideNumber) => {
          const quizData = completedQuizzes[slideNumber];
          if (quizData && quizData.completed) {
            quizStatuses[slideNumber] = {
              attempts: quizData.attempts || 0,
              status: quizData.passed ? "passed" : "failed",
              completed: quizData.completed || false,
              passed: quizData.passed || false,
            };
          }
        });

        const overriddenQuizStatuses = storage.overrideWithApiQuizStatuses(
          apiData.userProgress?.apiAllQuizStatuses || quizStatuses
        );

        // Also set React states for immediate UI responsiveness
        setAudioCompleted(completedSlides);
        setApiQuizStates(completedQuizzes);
        setStartSlide(apiStartSlide); // Store the start slide from API

        // Get isallSlidesCompleted from storage (set by dataService from API response)
        const isallSlidesCompleted = storage.getIsAllSlidesCompleted();

        console.log(
          "App: Initialized with API completion data (overridden localStorage):",
          {
            completedSlides,
            completedQuizzes,
            startSlide: apiStartSlide,
            localStorageCompletedSlides: overriddenCompletedSlides,
            localStorageQuizStatuses: overriddenQuizStatuses,
            isallSlidesCompleted: isallSlidesCompleted,
            // Enhanced tracking from new API fields
            apiCompletedSlides: apiData.userProgress?.apiCompletedSlides,
            apiAllQuizStatuses: apiData.userProgress?.apiAllQuizStatuses,
            apiIsAllSlidesCompleted: apiData.userProgress?.isallSlidesCompleted,
          }
        );

        console.log("App: Module data loaded successfully");

        // Send initial language to API after module data is loaded
        // Use validated locale from URL to ensure it's passed to the language API
        if (finalModuleId && accessToken) {
          // Get data from local storage for API call
          const allQuizStatuses = getAllQuizStatusesFromStorage();
          const completedSlides = getCompletedSlidesFromStorage();
          const isallSlidesCompleted = getIsAllSlidesCompletedFromStorage();

          // Send language change to API with validated locale
          dataService
            .saveProgressToAPI(
              {
                moduleId: finalModuleId,
                slideNumber: apiData.currentSlide + 1, // Current slide number (1-based for API)
                language: validatedLocale,
                allQuizStatuses: allQuizStatuses,
                completedSlides: completedSlides,
                isallSlidesCompleted: isallSlidesCompleted,
              },
              accessToken
            )
            .catch((error) => {
              // Continue with UI update even if API fails
              console.warn("Failed to save initial language:", error);
            });
        }
      } else {
        // Wait for minimum loading time before showing error
        await minLoadingTime;
        // No fallback - show error if API fails
        setApiError(apiData.error || "Failed to load module data");
      }

      // Check initial orientation
      checkOrientation();
    } catch (error) {
      console.error("App: Initialization failed:", error);
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Get locale from URL for error message
      const { locale: errorLocale } = getURLParameters();
      const localeValidation = validateLocale(errorLocale);
      const errorLocaleToUse = localeValidation.valid
        ? localeValidation.locale
        : "en";

      // Use dynamic error code from backend if available, otherwise fallback to hardcoded logic
      let errorTypeToSet = "elearning/app/default";
      
      if (error.code) {
        // Backend provided an error code - use it for CMS lookup
        errorTypeToSet = error.code;
      } else {
        // No backend error code - fallback to hardcoded logic based on error message
        const errorMessage = error.message;
        
        if (
          errorMessage.includes("Module not found") ||
          errorMessage.includes("404")
        ) {
          errorTypeToSet = "elearning/app/callback/404";
        } else if (
          errorMessage.includes("Authorization code is invalid") ||
          errorMessage.includes("invalid or has expired") ||
          errorMessage.includes("Auth code exchange failed")
        ) {
          errorTypeToSet = "elearning/app/callback/authmissing";
        }
      }

      // Fetch and set error message from CMS
      const errorData = await cmsService.getErrorMessage(
        errorTypeToSet,
        errorLocaleToUse
      );
      setApiError(errorData);
      setErrorType(errorTypeToSet);
    } finally {
      setIsLoading(false);
    }
  }, [
    getURLParameters,
    getAllQuizStatusesFromStorage,
    getCompletedSlidesFromStorage,
    getIsAllSlidesCompletedFromStorage,
  ]);

  // Load data from API when module ID or auth code changes
  useEffect(() => {
    initializeApp();
  }, [initializeApp]); // Re-run when URL parameters change

  // Handle orientation changes
  useEffect(() => {
    const handleOrientationChange = () => {
      checkOrientation();
    };

    const handleResize = () => {
      checkOrientation();
    };

    window.addEventListener("orientationchange", handleOrientationChange);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("orientationchange", handleOrientationChange);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const checkOrientation = () => {
    const isLandscapeMode = window.innerWidth > window.innerHeight;
    setIsLandscape(isLandscapeMode);
  };

  const handleLanguageChange = (languageCode) => {
    setCurrentLanguage(languageCode);

    // Update temporary UI state
    storage.setCurrentLanguage(languageCode);

    // Reset audio completion state for the current slide when language changes
    // since different languages may have different audio files
    const slides = lessonData?.Slides || lessonData?.slides || [];
    if (lessonData && slides.length > 0) {
      const currentSlide = slides[currentSlideIndex];
      const slideNumber =
        currentSlide?.slideNumber || currentSlide?.SlideNumber;
      if (slideNumber) {
        setAudioCompleted((prev) => ({
          ...prev,
          [slideNumber.toString()]: false,
        }));
      }
    }

    // Save language change to API (fire-and-forget, no await)
    if (moduleId) {
      // Get data from local storage for API call
      const allQuizStatuses = getAllQuizStatusesFromStorage();
      const completedSlides = getCompletedSlidesFromStorage();
      const isallSlidesCompleted = getIsAllSlidesCompletedFromStorage();

      // Send standardized API payload using local storage data
      dataService
        .saveProgressToAPI(
          {
            moduleId: moduleId,
            slideNumber: currentSlideIndex + 1, // Current slide number (1-based for API) - language change doesn't advance
            language: languageCode,
            allQuizStatuses: allQuizStatuses, // Format: {5: {attempts: 2, status: "passed"}}
            completedSlides: completedSlides, // Format: [1, 2, 3, 4, 5]
            isallSlidesCompleted: isallSlidesCompleted,
          },
          currentAccessToken
        )
        .catch((error) => {
          // Continue with UI update even if API fails
          console.warn("Failed to save language change:", error);
        });
    }
  };

  const handleAudioToggle = () => {
    setIsAudioMuted(!isAudioMuted);
  };

  const handleQuizStateChange = (slideNumber, newState) => {
    if (!slideNumber) {
      console.error("handleQuizStateChange: slideNumber is undefined");
      return;
    }

    // Update API quiz states (for immediate UI responsiveness)
    setApiQuizStates((prev) => ({
      ...prev,
      [slideNumber.toString()]: newState,
    }));

    // Update local storage for quiz status
    if (newState && (newState.attempts > 0 || newState.completed)) {
      let status = "in_progress";
      if (newState.completed) {
        status = newState.passed ? "passed" : "failed";
      }

      storage.updateQuizStatus(
        slideNumber,
        newState.attempts || 0,
        status,
        newState.completed || false,
        newState.passed || false
      );

      // Check if this is the final slide and quiz is completed
      const slides = lessonData?.Slides || lessonData?.slides || [];
      const isFinalSlide = currentSlideIndex === slides.length - 1;
      const isQuizCompleted = newState.completed === true;

      // If final slide quiz is completed, set isallSlidesCompleted to true
      let finalIsAllSlidesCompleted = getIsAllSlidesCompletedFromStorage();
      if (isFinalSlide && isQuizCompleted) {
        console.log(
          "Final slide quiz completed - setting isallSlidesCompleted to true"
        );
        storage.setIsAllSlidesCompleted(true);
        finalIsAllSlidesCompleted = true;
      }

      // Trigger API call when quiz is submitted
      if (moduleId && currentAccessToken) {
        // Get data from local storage for API call
        const allQuizStatuses = getAllQuizStatusesFromStorage();
        const completedSlides = getCompletedSlidesFromStorage();

        console.log("Quiz submission - sending API call:", {
          slideNumber: slideNumber,
          attempts: newState.attempts,
          status: status,
          completed: newState.completed,
          passed: newState.passed,
          isFinalSlide: isFinalSlide,
          isallSlidesCompleted: finalIsAllSlidesCompleted,
        });

        // Send quiz submission to API
        dataService
          .saveProgressToAPI(
            {
              moduleId: moduleId,
              slideNumber: currentSlideIndex + 1, // Current slide number (1-based for API)
              language: currentLanguage,
              allQuizStatuses: allQuizStatuses, // Format: {5: {attempts: 2, status: "passed"}}
              completedSlides: completedSlides, // Format: [1, 2, 3, 4, 5]
              isallSlidesCompleted: finalIsAllSlidesCompleted, // Use the final value (true if final slide completed)
            },
            currentAccessToken
          )
          .catch((error) => {
            console.warn("Failed to save quiz submission:", error);
          });
      }
    }

    // Trigger re-render when quiz state changes
    setQuizStateUpdate((prev) => prev + 1);
    console.log(`Quiz state changed for slide ${slideNumber}:`, newState);
  };

  // Helper function to calculate the next slide number based on completed slides
  const getNextSlideNumber = useCallback((completedSlidesArray) => {
    if (!completedSlidesArray || completedSlidesArray.length === 0) {
      return 1; // If no completed slides, next is slide 1
    }

    // Find the highest completed slide number and add 1
    const highestCompleted = Math.max(...completedSlidesArray);
    return highestCompleted + 1;
  }, []);

  const handleAudioComplete = useCallback(() => {
    const slides = lessonData?.Slides || lessonData?.slides || [];
    if (!lessonData || slides.length === 0) return;
    const currentSlide = slides[currentSlideIndex];
    const langCode = getLanguageCode(currentLanguage);

    const audioFiles = currentSlide?.audioFiles || currentSlide?.AudioFiles;
    if (
      currentSlide &&
      audioFiles &&
      audioFiles[langCode] &&
      audioFiles[langCode].length > 0
    ) {
      const slideNumber = currentSlide.slideNumber || currentSlide.SlideNumber;
      if (!slideNumber) return; // Exit if no slide number
      const slideId = slideNumber.toString();

      // Update in-memory state only (no localStorage)
      setAudioCompleted((prev) => ({
        ...prev,
        [slideId]: true,
      }));

      // Save audio completion to API (fire-and-forget, no await)
      if (moduleId) {
        // Mark current slide as completed in local storage
        const currentSlideNumber = currentSlideIndex + 1;
        storage.addCompletedSlide(currentSlideNumber);

        // Also update React state for immediate UI responsiveness
        setAudioCompleted((prev) => ({
          ...prev,
          [currentSlideNumber.toString()]: true,
        }));

        // Get data from local storage for API call (after adding current slide)
        const allQuizStatuses = getAllQuizStatusesFromStorage();
        const completedSlides = getCompletedSlidesFromStorage();

        // Ensure we maintain all previously completed slides from API + current session
        const allCompletedSlides = new Set(completedSlides);

        // Add slides before startSlide (these are always considered completed)
        for (let i = 1; i < startSlide; i++) {
          allCompletedSlides.add(i);
        }

        // Add current slide
        allCompletedSlides.add(currentSlideNumber);

        // Convert back to sorted array
        const finalCompletedSlides = Array.from(allCompletedSlides).sort(
          (a, b) => a - b
        );

        // Update localStorage with the complete set
        storage.setCompletedSlides(finalCompletedSlides);

        // Calculate the correct next slide number based on completed slides
        const nextSlideNumber = getNextSlideNumber(finalCompletedSlides);

        // Send standardized API payload with corrected completed slides and next slide number
        const isallSlidesCompleted = getIsAllSlidesCompletedFromStorage();
        dataService
          .saveProgressToAPI(
            {
              moduleId: moduleId,
              slideNumber: nextSlideNumber, // NEXT slide number based on highest completed + 1
              language: currentLanguage,
              allQuizStatuses: allQuizStatuses, // Format: {5: {attempts: 2, status: "passed"}}
              completedSlides: finalCompletedSlides, // Format: [1, 2, 3, 4, 5] - corrected array
              isallSlidesCompleted: isallSlidesCompleted,
            },
            currentAccessToken
          )
          .catch((error) => {
            // Continue with UI update even if API fails
            console.warn("Failed to save audio completion:", error);
          });
      }
    }
  }, [
    currentSlideIndex,
    currentLanguage,
    lessonData,
    moduleId,
    currentAccessToken,
    getAllQuizStatusesFromStorage,
    getCompletedSlidesFromStorage,
    getIsAllSlidesCompletedFromStorage,
    startSlide,
    getNextSlideNumber,
  ]);

  const canGoNext = () => {
    const slides = lessonData?.Slides || lessonData?.slides || [];
    if (!lessonData || slides.length === 0) return false;
    const currentSlide = slides[currentSlideIndex];
    if (!currentSlide) return false;

    const currentSlideNumber = currentSlideIndex + 1; // Convert to 1-based

    // IMPORTANT: If current slide is before the startSlide OR in completed slides, it's already completed
    // This allows users to navigate freely through previously completed slides
    const isBeforeStartSlide = currentSlideNumber < startSlide;
    const isInCompletedSlides = storage
      .getCompletedSlides()
      .includes(currentSlideNumber);

    if (isBeforeStartSlide || isInCompletedSlides) {
      console.log(
        `canGoNext: Slide ${currentSlideNumber} is completed (beforeStart: ${isBeforeStartSlide}, inStorage: ${isInCompletedSlides}), allowing navigation`
      );
      return true;
    }

    // If it's a quiz slide, check if it's completed (API data only)
    const isQuiz = currentSlide.isQuiz || currentSlide.IsQuiz;
    if (isQuiz) {
      const slideNumber = currentSlide.slideNumber || currentSlide.SlideNumber;
      if (slideNumber) {
        const quizState = apiQuizStates[slideNumber.toString()];
        // Only allow next if quiz is completed according to API data
        return quizState?.completed === true;
      }
      return false; // If no slide number, can't proceed
    }

    // For regular slides, check if audio is completed (API data only)
    const langCode = getLanguageCode(currentLanguage);
    const audioFiles = currentSlide.audioFiles || currentSlide.AudioFiles;
    if (audioFiles && audioFiles[langCode] && audioFiles[langCode].length > 0) {
      const slideNumber = currentSlide.slideNumber || currentSlide.SlideNumber;
      if (slideNumber) {
        const slideId = slideNumber.toString();
        // Only use in-memory audio completion state, no localStorage
        return audioCompleted[slideId] === true;
      }
      return false; // If no slide number, can't proceed
    }

    // If no audio, allow navigation
    return true;
  };

  const canGoPrevious = () => {
    return currentSlideIndex > 0;
  };

  const handleNext = () => {
    const slides = lessonData?.Slides || lessonData?.slides || [];
    if (!lessonData || slides.length === 0) return;

    // Check if we're on the last slide
    if (currentSlideIndex === slides.length - 1) {
      const currentSlide = slides[currentSlideIndex];
      const isQuiz = currentSlide.isQuiz || currentSlide.IsQuiz;

      if (!isQuiz) {
        // If it's a content slide (not a quiz), show success slide
        // Stop any playing audio before showing success slide
        if (stopAudioFunction) {
          stopAudioFunction();
        }

        // Send final progress to API before showing success slide
        if (moduleId) {
          // Get all previous quiz statuses from local storage
          const allQuizStatuses = getAllQuizStatusesFromStorage();
          const completedSlides = getCompletedSlidesFromStorage();

          // Set isallSlidesCompleted to true when reaching the final success slide
          storage.setIsAllSlidesCompleted(true);

          dataService
            .saveProgressToAPI(
              {
                moduleId: moduleId,
                slideNumber: currentSlideIndex + 1, // Convert to 1-based for API
                language: currentLanguage,
                allQuizStatuses: allQuizStatuses,
                completedSlides: completedSlides,
                isallSlidesCompleted: true, // Set to true when final success slide is reached
              },
              currentAccessToken
            )
            .catch((error) => {
              // Continue with UI update even if API fails
              console.warn("Failed to save final progress:", error);
            });
        }

        setShowSuccessSlide(true);
        return;
      } else {
        // If it's a quiz slide, check if the quiz is completed
        const slideNumber =
          currentSlide.slideNumber || currentSlide.SlideNumber;
        if (slideNumber) {
          const quizState = apiQuizStates[slideNumber.toString()];
          if (quizState?.completed) {
            // Quiz is completed (either passed or failed), show success slide
            // Stop any playing audio before showing success slide
            if (stopAudioFunction) {
              stopAudioFunction();
            }

            // Send final progress to API before showing success slide
            if (moduleId) {
              // Get all previous quiz statuses from local storage
              const allQuizStatuses = getAllQuizStatusesFromStorage();
              const completedSlides = getCompletedSlidesFromStorage();

              // Set isallSlidesCompleted to true when reaching the final success slide
              storage.setIsAllSlidesCompleted(true);

              dataService
                .saveProgressToAPI(
                  {
                    moduleId: moduleId,
                    slideNumber: currentSlideIndex + 1, // Convert to 1-based for API
                    language: currentLanguage,
                    allQuizStatuses: allQuizStatuses,
                    completedSlides: completedSlides,
                    isallSlidesCompleted: true, // Set to true when final success slide is reached
                  },
                  currentAccessToken
                )
                .catch((error) => {
                  // Continue with UI update even if API fails
                  console.warn("Failed to save final progress:", error);
                });
            }

            setShowSuccessSlide(true);
            return;
          }
        }
        // If quiz is not completed yet, do nothing (let user complete the quiz first)
        return;
      }
    }

    // Normal navigation to next slide
    if (currentSlideIndex < slides.length - 1) {
      const newIndex = currentSlideIndex + 1;

      // Stop any playing audio before navigating
      if (stopAudioFunction) {
        stopAudioFunction();
      }

      setCurrentSlideIndex(newIndex);

      // Reset quiz result showing state when navigating to a new slide
      setQuizShowingResults(false);

      // Save progress to API (fire-and-forget, no await)
      if (moduleId) {
        // Mark current slide as completed in local storage
        const currentSlideNumber = currentSlideIndex + 1;
        storage.addCompletedSlide(currentSlideNumber);

        // Also update React state for immediate UI responsiveness
        setAudioCompleted((prev) => {
          const newState = {
            ...prev,
            [currentSlideNumber.toString()]: true,
          };
          console.log("handleNext: Updated audioCompleted state:", newState);
          return newState;
        });

        // Get data from local storage for API call (after adding current slide)
        const allQuizStatuses = getAllQuizStatusesFromStorage();
        const completedSlides = getCompletedSlidesFromStorage();

        // Ensure we maintain all previously completed slides from API + current session
        // This fixes the issue where navigating backwards loses completion state
        const allCompletedSlides = new Set(completedSlides);

        // Add slides before startSlide (these are always considered completed)
        for (let i = 1; i < startSlide; i++) {
          allCompletedSlides.add(i);
        }

        // Add current slide
        allCompletedSlides.add(currentSlideNumber);

        // Convert back to sorted array
        const finalCompletedSlides = Array.from(allCompletedSlides).sort(
          (a, b) => a - b
        );

        // Update localStorage with the complete set
        storage.setCompletedSlides(finalCompletedSlides);

        console.log("handleNext: Using corrected completion data:", {
          allQuizStatuses,
          originalCompletedSlides: completedSlides,
          finalCompletedSlides: finalCompletedSlides,
        });

        // Calculate the correct next slide number based on completed slides
        const nextSlideNumber = getNextSlideNumber(finalCompletedSlides);

        // Send standardized API payload with corrected completed slides and next slide number
        const isallSlidesCompleted = getIsAllSlidesCompletedFromStorage();
        dataService
          .saveProgressToAPI(
            {
              moduleId: moduleId,
              slideNumber: nextSlideNumber, // NEXT slide number based on highest completed + 1
              language: currentLanguage,
              allQuizStatuses: allQuizStatuses, // Format: {5: {attempts: 2, status: "passed"}}
              completedSlides: finalCompletedSlides, // Format: [1, 2, 3, 4, 5] - corrected array
              isallSlidesCompleted: isallSlidesCompleted,
            },
            currentAccessToken
          )
          .catch((error) => {
            // Continue with UI update even if API fails
            console.warn("Failed to save progress:", error);
          });
      }
    }
  };

  const handlePrevious = () => {
    if (currentSlideIndex > 0) {
      // Stop any playing audio before navigating
      if (stopAudioFunction) {
        stopAudioFunction();
      }

      const slides = lessonData?.Slides || lessonData?.slides || [];
      const previousIndex = currentSlideIndex - 1;

      setCurrentSlideIndex(previousIndex);
      // Reset quiz result showing state when navigating to a new slide
      setQuizShowingResults(false);
    }
  };

  const handleSlideNavigate = (slideIndex) => {
    const slideNumber = slideIndex + 1; // Convert to 1-based
    const completedSlides = storage.getCompletedSlides();

    // Check if slide is accessible
    const isAccessible =
      slideIndex === currentSlideIndex ||
      completedSlides.includes(slideNumber) ||
      (completedSlides.length > 0 &&
        slideNumber <= Math.max(...completedSlides) + 1) ||
      (completedSlides.length === 0 && slideNumber === 1);

    if (
      isAccessible &&
      slideIndex >= 0 &&
      slideIndex < (lessonData?.Slides?.length || 0)
    ) {
      // Stop any playing audio before navigating
      if (stopAudioFunction) {
        stopAudioFunction();
      }

      const slides = lessonData?.Slides || lessonData?.slides || [];

      setCurrentSlideIndex(slideIndex);

      // Reset quiz result showing state when navigating to a new slide
      setQuizShowingResults(false);

      console.log(`Navigated to slide ${slideNumber} via dropdown`);
    } else {
      console.warn(`Slide ${slideNumber} is not accessible`);
    }
  };

  const handleRevisit = (sessionStartIndex) => {
    console.log("Revisiting slide index:", sessionStartIndex);

    // Stop any playing audio before navigating
    if (stopAudioFunction) {
      stopAudioFunction();
    }

    const slides = lessonData?.Slides || lessonData?.slides || [];

    // Force a language reload to ensure audio gets properly reloaded
    // This triggers the audio system to reset and reload correctly
    const currentLang = currentLanguage;
    setCurrentLanguage(""); // Temporarily clear language

    // Set the slide index
    setCurrentSlideIndex(sessionStartIndex);

    // Reset quiz result showing state when navigating to a new slide
    setQuizShowingResults(false);

    // Force quiz state update to ensure clean transition
    setQuizStateUpdate((prev) => prev + 1);

    // Restore the language after a brief delay to trigger reload
    setTimeout(() => {
      setCurrentLanguage(currentLang);
    }, 100);
  };

  const handleGoToSlide1 = () => {
    console.log("Navigating to slide 1");

    // Stop any playing audio before navigating
    if (stopAudioFunction) {
      stopAudioFunction();
    }

    const slides = lessonData?.Slides || lessonData?.slides || [];
    // Set the slide index to 0 (slide 1)
    setCurrentSlideIndex(0);

    // Reset quiz result showing state when navigating to a new slide
    setQuizShowingResults(false);

    // Force quiz state update to ensure clean transition
    setQuizStateUpdate((prev) => prev + 1);

    // Force audio completion state reset for slide 1
    setAudioCompleted((prev) => ({
      ...prev,
      1: false, // Reset slide 1 audio completion to ensure it can play again
    }));

    console.log("Successfully navigated to slide 1");
  };

  // Calculate the correct slide to revisit from based on current quiz slide
  const calculateRevisitSlide = (currentQuizSlideIndex) => {
    const slides = lessonData?.Slides || lessonData?.slides || [];
    if (slides.length === 0) return 0;

    // Find all quiz slides before the current one
    let previousQuizIndex = -1;
    for (let i = currentQuizSlideIndex - 1; i >= 0; i--) {
      const slide = slides[i];
      if (slide.isQuiz || slide.IsQuiz) {
        previousQuizIndex = i;
        break;
      }
    }

    // If there's a previous quiz, start from the slide right after it
    // If no previous quiz, start from the beginning (slide 0)
    const revisitSlideIndex =
      previousQuizIndex >= 0 ? previousQuizIndex + 1 : 0;

    console.log(
      `Quiz at slide ${currentQuizSlideIndex + 1}: Previous quiz at slide ${
        previousQuizIndex + 1
      }, revisiting from slide ${revisitSlideIndex + 1}`
    );

    return revisitSlideIndex;
  };

  const handleExit = () => {
    // Show custom confirmation dialog
    setShowExitConfirmation(true);
  };

  const handleExitConfirm = async () => {
    // User confirmed exit - proceed with exit logic
    try {
      if (moduleId && currentAccessToken) {
        // Get data from local storage for API call (same as Next button)
        const allQuizStatuses = getAllQuizStatusesFromStorage();
        const completedSlides = getCompletedSlidesFromStorage();

        // Calculate the correct next slide number based on completed slides (same as Next button)
        const nextSlideNumber = getNextSlideNumber(completedSlides);

        console.log("Exit: Sending API call (same as Next button):", {
          slideNumber: nextSlideNumber,
          allQuizStatuses,
          completedSlides,
        });

        // Send standardized API payload (same as Next button - no exit event)
        const isallSlidesCompleted = getIsAllSlidesCompletedFromStorage();
        await dataService.saveProgressToAPI(
          {
            moduleId: moduleId,
            slideNumber: nextSlideNumber, // NEXT slide number based on highest completed + 1
            language: currentLanguage,
            allQuizStatuses: allQuizStatuses, // Format: {5: {attempts: 2, status: "passed"}}
            completedSlides: completedSlides, // Format: [1, 2, 3, 4, 5]
            isallSlidesCompleted: isallSlidesCompleted,
          },
          currentAccessToken
        );

        console.log("Exit: API call successful");
      }
    } catch (error) {
      console.warn("Error saving exit data:", error);
    } finally {
      // Handle exit functionality
      // Reset success slide state if it's showing
      setShowSuccessSlide(false);

      // Close the current browser tab - Two methods only
      console.log("Attempting to close tab...");

      // Method 1: Try to close the tab
      window.close();

      // Method 2: If close doesn't work, go back
      setTimeout(() => {
        if (!window.closed) {
          console.log("Close failed, going back...");
          window.history.back();
        }
      }, 100);
    }
  };

  const handleExitCancel = () => {
    // User cancelled exit - hide confirmation dialog
    setShowExitConfirmation(false);
  };

  // Try different possible slide array names
  const slidesArray = lessonData?.Slides || lessonData?.slides || [];
  const currentSlide = slidesArray[currentSlideIndex];

  // Debug: Log current slide detection (only if no slide found)
  if (!currentSlide) {
    console.log("App: No slide found - Debug info:", {
      lessonData: !!lessonData,
      slidesArray: slidesArray.length,
      currentSlideIndex,
      lessonDataKeys: lessonData ? Object.keys(lessonData) : "no lesson data",
    });
  }

  // Determine audio source based on slide type
  let audioSrc = null;

  // Only process audio for current slide if it exists
  if (currentSlide) {
    console.log("App.js: Processing audio for slide:", currentSlide);

    const isQuiz = currentSlide.isQuiz || currentSlide.IsQuiz;
    if (isQuiz) {
      // Get all audio files for quiz (try both property names)
      const audioFiles = currentSlide.audioFiles || currentSlide.AudioFiles;
      console.log("App.js: Quiz audio files:", audioFiles);

      // Try both language code formats
      const langCodeUpper = getLanguageCode(currentLanguage);
      const langCodeLower = currentLanguage;
      const allAudios =
        audioFiles?.[langCodeUpper] || audioFiles?.[langCodeLower] || [];

      console.log(
        "App.js: Quiz audio for language",
        langCodeUpper,
        langCodeLower,
        ":",
        allAudios
      );

      // Debug: Check each audio URL for potential issues
      if (allAudios.length > 0) {
        console.log("App.js: Debugging quiz audio URLs:");
        allAudios.forEach((url, index) => {
          console.log(`  Audio ${index}:`, url);
          console.log(`  Audio ${index} type:`, typeof url);
          if (typeof url === "string") {
            console.log(
              `  Audio ${index} starts with http:`,
              url.startsWith("http")
            );
            if (url.includes("%3A2A")) {
              console.warn(`  Audio ${index} has malformed encoding:`, url);
            }
          }
        });
      }

      if (allAudios.length > 0) {
        // Get current quiz state to determine attempt number
        const slideNumber =
          currentSlide.slideNumber || currentSlide.SlideNumber;
        if (!slideNumber) {
          console.warn("Quiz slide missing slideNumber, skipping audio setup");
          return null;
        }
        const quizState = apiQuizStates[slideNumber.toString()] || {
          attempts: 0,
          completed: false,
        };
        const currentAttempt = quizState.attempts || 0;

        // Only provide quiz audio if quiz is not completed
        if (!quizState.completed) {
          // Map audio based on attempt number (0-based):
          // Attempt 0 (1st attempt): audios 0-3
          // Attempt 1 (2nd attempt): audios 4-7
          // Attempt 2 (3rd attempt): audios 8-11
          const startIndex = currentAttempt * 4;
          let rawAudioSrc = allAudios.slice(startIndex, startIndex + 4);

          // Fix any malformed URLs
          audioSrc = rawAudioSrc.map((url, index) => {
            if (
              typeof url === "string" &&
              (url.includes("%3A2A") || url.includes("T13%3A2A"))
            ) {
              const fixedUrl = url
                .replace(/%3A2A/g, "%3A")
                .replace(/T13%3A2A/g, "T13%3A");
              console.log(
                `App.js: Fixed malformed URL ${index}:`,
                url,
                "->",
                fixedUrl
              );
              return fixedUrl;
            }
            return url;
          });

          console.log(
            "App.js: Quiz audio queue for attempt",
            currentAttempt,
            ":",
            audioSrc
          );
        } else {
          audioSrc = null;
          console.log("App.js: Quiz completed, no audio");
        }
      }
    } else {
      // For regular slides, just get the first audio file
      const langCodeUpper = getLanguageCode(currentLanguage);
      const langCodeLower = currentLanguage;
      const audioFiles = currentSlide.audioFiles || currentSlide.AudioFiles;

      console.log("App.js: Regular slide audio files:", audioFiles);

      // Try both language code formats
      audioSrc =
        audioFiles?.[langCodeUpper]?.[0] ||
        audioFiles?.[langCodeLower]?.[0] ||
        null;

      console.log(
        "App.js: Regular slide audio for language",
        langCodeUpper,
        langCodeLower,
        ":",
        audioSrc
      );
    }
  }

  // Show landscape warning if not in landscape mode
  if (!isLandscape) {
    return <LandscapeWarning currentLanguage={currentLanguage} />;
  }

  // Show loading state
  if (isLoading) {
    return <LoadingScreen currentLanguage={currentLanguage} />;
  }

  // Show error state - but allow exit confirmation to overlay
  if (apiError && !lessonData) {
    return (
      <>
        <ErrorDisplay
          error={apiError}
          errorType={errorType}
          currentLanguage={currentLanguage}
          onExit={handleExit}
        />

        {/* Exit confirmation overlay - can appear over error display */}
        {showExitConfirmation && (
          <ExitConfirmation
            currentLanguage={currentLanguage}
            onConfirm={handleExitConfirm}
            onCancel={handleExitCancel}
          />
        )}
      </>
    );
  }

  // Do not early-return for success; render it as an overlay so other overlays (like exit confirm) still work

  // Don't return early for exit confirmation - show as overlay instead

  return (
    <div className={getAppClasses()}>
      {/* Hide TopBar for quiz slides */}
      {!currentSlide?.isQuiz && (
        <TopBar
          currentLanguage={currentLanguage}
          onLanguageChange={handleLanguageChange}
          isAudioMuted={isAudioMuted}
          onAudioToggle={handleAudioToggle}
          currentSlideIndex={currentSlideIndex}
          totalSlides={lessonData?.Slides?.length || 0}
          onVisibilityChange={handleTopBarVisibilityChange}
          onSlideNavigate={handleSlideNavigate}
          completedSlides={storage.getCompletedSlides()}
          lessonData={lessonData}
        />
      )}

      <main className="main-content">
        {currentSlide ? (
          <>
            {currentSlide.isQuiz || currentSlide.IsQuiz ? (
              <QuizSlide
                key={`quiz-${currentSlideIndex}-${currentLanguage}`} // Force re-render on language change
                slide={currentSlide}
                currentLanguage={currentLanguage}
                onNext={handleNext}
                onPrevious={handlePrevious}
                onGoToSlide1={handleGoToSlide1}
                onExit={handleExit}
                onRevisit={handleRevisit}
                calculateRevisitSlide={calculateRevisitSlide}
                currentSlideIndex={currentSlideIndex}
                onQuizStateChange={handleQuizStateChange}
                stopAudioFunction={stopAudioFunction}
                setQuizShowingResults={setQuizShowingResults}
                canGoNext={canGoNext()}
                canGoPrevious={canGoPrevious()}
                audioSrc={audioSrc}
                onAudioComplete={handleAudioComplete}
                isAudioMuted={isAudioMuted}
                onAudioToggle={handleAudioToggle}
                onLanguageChange={handleLanguageChange}
                onStopAudio={setStopAudioFunction}
                isQuizAudioDisabled={getQuizAudioDisabled()}
                totalSlides={slidesArray.length}
                initialQuizState={
                  apiQuizStates[
                    (
                      currentSlide?.SlideNumber || currentSlide?.slideNumber
                    )?.toString()
                  ] || null
                }
              />
            ) : (
              <ContentSlide
                slide={currentSlide}
                currentLanguage={currentLanguage}
              />
            )}
          </>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              color: "#fff",
              fontSize: "1.2rem",
            }}
          >
            {lessonData ? "Loading slide content..." : "Loading module data..."}
          </div>
        )}
      </main>

      {/* Hide AudioBar for quiz slides */}
      {!currentSlide?.isQuiz && (
        <AudioBar
          audioSrc={audioSrc}
          onAudioComplete={handleAudioComplete}
          isAudioMuted={isAudioMuted}
          onNext={handleNext}
          onPrevious={handlePrevious}
          onExit={handleExit}
          canGoNext={canGoNext()}
          canGoPrevious={canGoPrevious()}
          currentSlide={currentSlide}
          currentLanguage={currentLanguage}
          onStopAudio={setStopAudioFunction}
          isQuizAudioDisabled={getQuizAudioDisabled()}
          onVisibilityChange={handleAudioBarVisibilityChange}
          isSlideCompleted={(() => {
            const currentSlideNumber = currentSlideIndex + 1;
            // Check if slide is before startSlide OR if it's in localStorage completed slides
            const isBeforeStartSlide = currentSlideNumber < startSlide;
            const isInCompletedSlides = storage
              .getCompletedSlides()
              .includes(currentSlideNumber);
            return isBeforeStartSlide || isInCompletedSlides;
          })()} // Pass if slide is already completed (before startSlide OR in localStorage)
        />
      )}

      {/* Success Overlay */}
      {showSuccessSlide && (
        <SuccessSlide
          currentLanguage={currentLanguage}
          onGoToSlide1={handleGoToSlide1FromSuccess}
          onExit={handleExit}
        />
      )}

      {/* Exit Confirmation Overlay */}
      {showExitConfirmation && (
        <ExitConfirmation
          currentLanguage={currentLanguage}
          onConfirm={handleExitConfirm}
          onCancel={handleExitCancel}
        />
      )}
    </div>
  );
}

export default App;
